"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useContextElement } from "@/context/Context";
import { useT } from "@/context/I18nContext";
import { usePWA } from "@/utlis/usePWA";
import { useBottomBarVisible } from "@/utlis/useBottomBarVisible";
import { useIsProductPage } from "@/utlis/useIsProductPage";

export default function BottomNav({ userState }) {
	const isPWA = usePWA();
	const pathname = usePathname();
	const isProductPage = useIsProductPage();

	const { cartProducts, wishList, setChatOpen } = useContextElement();
	const { t } = useT();

	const cartCount = cartProducts.reduce((sum, item) => sum + (item.quantity || 0), 0);
	const wishCount = wishList.length;

	// bara se ascunde la scroll DOAR pe pagina de produs
	const visible = useBottomBarVisible(isPWA && isProductPage);

	if (!isPWA) return null;

	const normalizePath = (path) => {
		if (!path) return "/";

		const clean = path.split("?")[0].split("#")[0];

		if (clean.length > 1 && clean.endsWith("/")) {
			return clean.slice(0, -1);
		}

		return clean;
	};

	const currentPath = normalizePath(pathname);

	const isActive = (href) => {
		const current = normalizePath(currentPath);
		const target = normalizePath(href);

		// HOME
		if (target === "/") {
			return current === "/";
		}

		// restul paginilor
		return current === target || current.startsWith(`${target}/`);
	};

	const tabs = [
		{
			href: "/",
			label: t("acasa") || "Acasă",

			icon: (active) => (
				<svg
					width='26'
					height='26'
					viewBox='0 0 24 24'
					fill='none'
					stroke={active ? "#0d6efd" : "#111827"}
					strokeWidth={active ? "2.2" : "1.8"}
					strokeLinecap='round'
					strokeLinejoin='round'>
					<path
						d='M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z'
						fill={active ? "#0d6efd" : "none"}
						fillOpacity={active ? "0.15" : "0"}
					/>

					<path d='M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1-1H4a1 1 0 0 1-1-1V9.5z' />

					<polyline points='9 21 9 13 15 13 15 21' />
				</svg>
			),
		},

		{
			href: "/wishlist",
			label: t("favorite") || "Favorite",
			badge: wishCount,

			icon: (active) => (
				<svg
					width='26'
					height='26'
					viewBox='0 0 24 24'
					fill='none'
					stroke={active ? "#0d6efd" : "#111827"}
					strokeWidth={active ? "2.2" : "1.8"}
					strokeLinecap='round'
					strokeLinejoin='round'>
					<path
						d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'
						fill={active ? "#0d6efd" : "none"}
						fillOpacity={active ? "0.15" : "0"}
					/>

					<path d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' />
				</svg>
			),
		},

		{
			href: "/cos-de-cumparaturi",
			label: "Coș",
			badge: cartCount,

			icon: (active) => (
				<svg
					width='26'
					height='26'
					viewBox='0 0 24 24'
					fill='none'
					stroke={active ? "#0d6efd" : "#111827"}
					strokeWidth={active ? "2.2" : "1.8"}
					strokeLinecap='round'
					strokeLinejoin='round'>
					<path
						d='M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z'
						fill={active ? "#0d6efd" : "none"}
						fillOpacity={active ? "0.12" : "0"}
					/>

					<path d='M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z' />

					<line x1='3' y1='6' x2='21' y2='6' />

					<path d='M16 10a4 4 0 0 1-8 0' />
				</svg>
			),
		},

		{
			href: userState ? "/contul-meu" : "/autentificare",
			label: "Cont",

			icon: (active) => (
				<svg
					width='26'
					height='26'
					viewBox='0 0 24 24'
					fill='none'
					stroke={active ? "#0d6efd" : "#111827"}
					strokeWidth={active ? "2.2" : "1.8"}
					strokeLinecap='round'
					strokeLinejoin='round'>
					<circle
						cx='12'
						cy='8'
						r='4'
						fill={active ? "#0d6efd" : "none"}
						fillOpacity={active ? "0.15" : "0"}
					/>

					<circle cx='12' cy='8' r='4' />

					<path d='M4 20c0-4 3.6-7 8-7s8 3 8 7' />
				</svg>
			),
		},
	];

	return (
		<>
			<style jsx>{`
				/* =========================================================
				   SPACER
				   ========================================================= */

				.bnav-spacer {
					width: 100%;
					height: 100px;
					pointer-events: none;
				}

				/* =========================================================
				   FIXED WRAPPER
				   ========================================================= */

				.bnav-wrap {
					position: fixed;

					left: 0;
					right: 0;

					bottom: calc(env(safe-area-inset-bottom) + 14px);

					z-index: 99999;

					width: 100%;

					display: flex;
					justify-content: center;

					padding: 0 16px;

					pointer-events: none;
				}

				/* =========================================================
				   IMPORTANT:
				   NAV + ROBOT SUNT GRID.

				   Nu mai folosim padding-right: 76px.
				   ========================================================= */

				.bnav-row {
					width: 100%;
					max-width: 480px;

					display: grid;

					grid-template-columns:
						minmax(0, 1fr)
						68px;

					column-gap: 8px;

					align-items: center;

					position: relative;

					pointer-events: none;
				}

				/* =========================================================
				   BOTTOM PILL
				   ========================================================= */

				.bnav-pill {
					width: 100%;
					height: 68px;

					min-width: 0;

					display: flex;

					align-items: stretch;
					justify-content: space-between;

					padding: 6px;

					margin: 0;

					background: #ffffff;

					border: 0;

					border-radius: 34px;

					box-shadow:
						0 10px 30px rgba(15, 23, 42, 0.14),
						0 2px 8px rgba(15, 23, 42, 0.06);

					overflow: hidden;

					pointer-events: auto;
				}

				/* =========================================================
				   EACH TAB

				   IMPORTANT:
				   flex-basis 0 + width 25%
				   => cele 4 tab-uri au aceeași lățime.
				   ========================================================= */

				.bnav-tab {
					flex: 1 1 0;

					width: 25%;
					min-width: 0;

					height: 56px;

					margin: 0;

					padding: 5px 3px;

					box-sizing: border-box;

					display: flex;

					flex-direction: column;

					align-items: center;
					justify-content: center;

					gap: 3px;

					position: relative;

					border: 0;
					border-radius: 28px;

					background: transparent;

					color: #4b5563;

					text-decoration: none;

					text-align: center;

					outline: none;

					cursor: pointer;

					-webkit-tap-highlight-color: transparent;

					transition:
						background-color 0.2s ease,
						color 0.2s ease,
						box-shadow 0.2s ease,
						transform 0.15s ease;
				}

				/* =========================================================
				   ACTIVE
				   ========================================================= */

				.bnav-tab.active {
					background: #eef0f3 !important;

					color: #0d6efd !important;

					box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
				}

				/* =========================================================
				   HOVER
				   ========================================================= */

				.bnav-tab:not(.active):hover {
					color: #0d6efd;
				}

				/* =========================================================
				   ACTIVE PRESS
				   ========================================================= */

				.bnav-tab:active {
					transform: scale(0.96);
				}

				/* =========================================================
				   REMOVE FOCUS BORDER
				   ========================================================= */

				.bnav-tab:focus,
				.bnav-tab:focus-visible {
					outline: none;
				}

				/* =========================================================
				   ICON WRAPPER
				   ========================================================= */

				.bnav-icon-wrap {
					position: relative;

					width: 100%;
					height: 27px;

					display: flex;

					align-items: center;
					justify-content: center;

					flex: 0 0 27px;

					margin: 0;
					padding: 0;

					color: inherit;
				}

				/* =========================================================
				   SVG

				   Punem dimensiunile explicit pentru a evita
				   orice CSS global al temei.
				   ========================================================= */

				.bnav-icon-wrap svg {
					display: block;

					width: 26px !important;
					height: 26px !important;

					min-width: 26px !important;
					min-height: 26px !important;

					max-width: 26px !important;
					max-height: 26px !important;

					margin: 0;
					padding: 0;

					flex: 0 0 26px;

					color: inherit;

					stroke: currentColor;
				}

				/* =========================================================
				   LABEL

				   Centrare REALĂ.
				   ========================================================= */

				.bnav-label {
					display: block;

					width: 100%;

					height: 11px;

					margin: 0;
					padding: 0;

					box-sizing: border-box;

					color: inherit;

					font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;

					font-size: 10.5px;

					font-weight: 500;

					line-height: 11px;

					text-align: center;

					white-space: nowrap;

					overflow: hidden;

					text-overflow: ellipsis;
				}

				.bnav-tab.active .bnav-label {
					color: #0d6efd !important;
					font-weight: 700;
				}

				/* =========================================================
				   BADGE
				   ========================================================= */

				.bnav-badge {
					position: absolute;

					top: -2px;

					right: calc(50% - 20px);

					min-width: 16px;
					height: 16px;

					padding: 0 4px;

					box-sizing: border-box;

					display: flex;

					align-items: center;
					justify-content: center;

					border-radius: 999px;

					background: #ef4444;

					color: #ffffff !important;

					font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;

					font-size: 9px;

					font-weight: 700;

					line-height: 1;

					border: 2px solid #ffffff;

					z-index: 20;

					pointer-events: none;
				}

				/* =========================================================
				   ROBOT

				   EXACT SAME HEIGHT AS NAV
				   ========================================================= */

				.robot-button {
					width: 68px;
					height: 68px;

					margin: 0;
					padding: 0;

					box-sizing: border-box;

					border: 0;

					border-radius: 50%;

					background: #ffffff;

					box-shadow:
						0 10px 30px rgba(15, 23, 42, 0.14),
						0 2px 8px rgba(15, 23, 42, 0.06);

					display: flex;

					align-items: center;
					justify-content: center;

					cursor: pointer;

					pointer-events: auto;

					z-index: 20;

					outline: none;

					-webkit-tap-highlight-color: transparent;

					transition:
						transform 0.15s ease,
						box-shadow 0.2s ease;
				}

				.robot-button:hover {
					box-shadow:
						0 12px 34px rgba(15, 23, 42, 0.18),
						0 3px 10px rgba(15, 23, 42, 0.08);
				}

				.robot-button:active {
					transform: scale(0.92);
				}

				.robot-button:focus,
				.robot-button:focus-visible {
					outline: none;
				}

				/* =========================================================
				   ROBOT SVG
				   ========================================================= */

				.robot-icon {
					display: block;

					width: 36px !important;
					height: 36px !important;

					min-width: 36px !important;
					min-height: 36px !important;

					max-width: 36px !important;
					max-height: 36px !important;

					margin: 0;
					padding: 0;

					flex: 0 0 36px;
				}

				/* =========================================================
				   SMALL PHONES
				   ========================================================= */

				@media (max-width: 380px) {
					.bnav-wrap {
						padding-left: 10px;
						padding-right: 10px;
					}

					.bnav-row {
						grid-template-columns:
							minmax(0, 1fr)
							58px;

						column-gap: 8px;
					}

					.robot-button {
						width: 58px;
						height: 58px;
					}

					.robot-icon {
						width: 32px !important;
						height: 32px !important;

						min-width: 32px !important;
						min-height: 32px !important;

						max-width: 32px !important;
						max-height: 32px !important;

						flex-basis: 32px;
					}

					.bnav-pill {
						height: 58px;

						padding: 5px;

						border-radius: 29px;
					}

					.bnav-tab {
						height: 48px;

						padding-left: 2px;
						padding-right: 2px;

						border-radius: 24px;
					}

					.bnav-icon-wrap {
						height: 24px;

						flex-basis: 24px;
					}

					.bnav-icon-wrap svg {
						width: 23px !important;
						height: 23px !important;

						min-width: 23px !important;
						min-height: 23px !important;

						max-width: 23px !important;
						max-height: 23px !important;

						flex-basis: 23px;
					}

					.bnav-label {
						font-size: 10px;

						height: 10px;

						line-height: 10px;
					}
				}

				/* =========================================================
				   VERY SMALL PHONES
				   ========================================================= */

				@media (max-width: 340px) {
					.bnav-wrap {
						padding-left: 7px;
						padding-right: 7px;
					}

					.bnav-row {
						grid-template-columns:
							minmax(0, 1fr)
							54px;

						column-gap: 6px;
					}

					.robot-button {
						width: 54px;
						height: 54px;
					}

					.robot-icon {
						width: 29px !important;
						height: 29px !important;

						min-width: 29px !important;
						min-height: 29px !important;

						max-width: 29px !important;
						max-height: 29px !important;

						flex-basis: 29px;
					}
				}
			`}</style>

			{/* =========================================================
			    SPACER
			    ========================================================= */}

			<div className='bnav-spacer' aria-hidden='true' />

			{/* =========================================================
			    BOTTOM NAV
			    ========================================================= */}

			<div
				className='bnav-wrap'
				style={{
					transform: visible ? "translateY(0)" : "translateY(140%)",
					transition: "transform 0.28s ease",
				}}>
				<div className='bnav-row'>
					{/* =====================================================
					    NAV
					    ===================================================== */}

					<div className='bnav-pill' role='navigation' aria-label='Navigare principală'>
						{tabs.map((tab) => {
							const active = isActive(tab.href);

							return (
								<Link
									key={tab.href}
									href={tab.href}
									className='bnav-tab'
									aria-label={String(tab.label)}
									aria-current={active ? "page" : undefined}
									style={{
										flex: "1 1 0",
										width: "25%",
										minWidth: 0,
										height: 56,
										boxSizing: "border-box",
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										gap: 3,
										padding: "5px 3px",
										borderRadius: 28,
										textDecoration: "none",
										textAlign: "center",
										position: "relative",
										backgroundColor: active ? "#eef0f3" : "#ffffff",
										color: active ? "#0d6efd" : "#111827",
										boxShadow: active ? "0 1px 4px rgba(15, 23, 42, 0.12)" : "none",
									}}>
									<div
										className='bnav-icon-wrap'
										style={{
											position: "relative",
											width: "100%",
											height: 27,
											flex: "0 0 27px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											margin: 0,
											padding: 0,
										}}>
										{tab.icon(active)}

										{tab.badge > 0 && (
											<span className='bnav-badge'>
												{tab.badge > 99 ? "99+" : tab.badge}
											</span>
										)}
									</div>

									<span
										className='bnav-label'
										style={{
											display: "block",
											width: "100%",
											height: 11,
											lineHeight: "11px",
											margin: 0,
											padding: 0,
											fontSize: 10.5,
											textAlign: "center",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											color: active ? "#0d6efd" : "#111827",
											fontWeight: active ? 700 : 500,
										}}>
										{tab.label}
									</span>
								</Link>
							);
						})}
					</div>

					{/* =====================================================
					    ROBOT
					    ===================================================== */}

					<button
						className='robot-button'
						type='button'
						aria-label='Asistent'
						onClick={() => setChatOpen((v) => !v)}>
						<svg
							className='robot-icon'
							viewBox='0 0 48 48'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
							aria-hidden='true'>
							<path d='M24 8V4' stroke='#0d6efd' strokeWidth='2.5' strokeLinecap='round' />

							<circle cx='24' cy='3' r='2' fill='#0d6efd' />

							<rect
								x='8'
								y='10'
								width='32'
								height='25'
								rx='8'
								fill='#eef0f3'
								stroke='#0d6efd'
								strokeWidth='2'
							/>

							<circle cx='18' cy='21' r='3' fill='#0d6efd' />

							<circle cx='30' cy='21' r='3' fill='#0d6efd' />

							<path
								d='M18 28C20 30 28 30 30 28'
								stroke='#0d6efd'
								strokeWidth='2'
								strokeLinecap='round'
							/>

							<path
								d='M15 35V39C15 41 17 43 19 43H29C31 43 33 41 33 39V35'
								stroke='#0d6efd'
								strokeWidth='2'
								strokeLinecap='round'
							/>
						</svg>
					</button>
				</div>
			</div>
		</>
	);
}
